import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Calendar, Image as ImageIcon, Wallet, Sparkles, HandHeart, Heart, Baby, Cake, GraduationCap, Plane, Gift, Lightbulb, Cloud, Stethoscope, LifeBuoy, AlertTriangle, Users } from 'lucide-react';
import { CoverUpload } from './CoverUpload';
import { joyeuseCategories, yakoCategories } from '../../mock';

export const iconMap = { Heart, Baby, Cake, GraduationCap, Plane, Gift, Lightbulb, Sparkles, Cloud, Stethoscope, LifeBuoy, AlertTriangle, Users, HandHeart };

export const DEFAULT_IMAGES = {
  Joyeuse: 'https://images.unsplash.com/photo-1647616927583-1d44a79a38a5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  Yako: 'https://images.unsplash.com/photo-1655720359248-eeace8c709c5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
};

export const COUNTRIES = [
  "Côte d'Ivoire", 'Sénégal', 'Mali', 'Burkina Faso', 'Togo', 'Bénin', 'Cameroun', 'Guinée', 'Niger', 'France', 'Belgique', 'Canada',
];

export const PROVIDERS = [
  { id: 'wave', name: 'Wave', color: '#1B6BF7' },
  { id: 'orange', name: 'Orange Money', color: '#FF6B00' },
  { id: 'mtn', name: 'MTN MoMo', color: '#F8C108' },
  { id: 'moov', name: 'Moov Money', color: '#3DBA75' },
];

export const DURATION_PRESETS = [15, 30, 60, 90];
export const GOAL_PRESETS = [50000, 100000, 200000, 500000, 1000000];

export const formatN = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);

function TypeTile({ active, onClick, Icon, title, desc, color }) {
  return (
    <button onClick={onClick} className={`rounded-2xl border p-4 text-left btn-anim ${active ? 'bg-white shadow-sm' : 'bg-white/60 border-black/5 hover:bg-white'}`} style={active ? { borderColor: color } : {}}>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}18`, color }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="mt-3 font-semibold text-[15px] text-[#1a1d1a]">{title}</div>
      <div className="text-[12.5px] text-neutral-600">{desc}</div>
    </button>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-center rounded-lg bg-neutral-50 border border-black/5 px-3 py-2">
      <span className="text-neutral-500">{label}</span>
      <span className="font-medium text-neutral-900 truncate ml-2">{value}</span>
    </div>
  );
}

export function StepCategory({ type, setType, category, setCategory, accent }) {
  const cats = type === 'Yako' ? yakoCategories : joyeuseCategories;
  return (
    <div className="space-y-5">
      <div>
        <div className="text-[13px] font-medium text-neutral-700 mb-2">Type de cagnotte</div>
        <div className="grid grid-cols-2 gap-3">
          <TypeTile active={type === 'Joyeuse'} onClick={() => setType('Joyeuse')} Icon={Sparkles} title="Joyeuse" desc="Célébrer un moment heureux" color="#d97757" />
          <TypeTile active={type === 'Yako'} onClick={() => setType('Yako')} Icon={HandHeart} title="Yako" desc="Soutenir dans l'épreuve" color="#5a8268" />
        </div>
      </div>
      <div>
        <div className="text-[13px] font-medium text-neutral-700 mb-2">Choisissez une catégorie</div>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
          {cats.map((c) => {
            const Icon = iconMap[c.icon] || Sparkles;
            const active = category === c.label;
            return (
              <button
                key={c.label}
                onClick={() => setCategory(c.label)}
                className={`rounded-2xl border p-3.5 flex flex-col items-center gap-2 btn-anim ${active ? 'bg-white shadow-sm' : 'bg-white/60 border-black/5 hover:bg-white'}`}
                style={active ? { borderColor: accent } : {}}
              >
                <Icon className="w-5 h-5" style={{ color: accent }} />
                <span className="text-[12.5px] font-medium text-neutral-800">{c.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function StepStory({ type, title, setTitle, description, setDescription, image, setImage }) {
  const placeholder = type === 'Yako'
    ? "Ex : Soutien aux obs\u00e8ques de Papa Kouassi"
    : "Ex : Mariage de Awa & Ibrahim";
  return (
    <div className="space-y-5">
      <div>
        <Label className="text-[13px]">Titre de la cagnotte</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={placeholder} className="mt-1.5 h-11" maxLength={80} />
        <div className="text-[11px] text-neutral-500 mt-1">{title.length}/80</div>
      </div>
      <div>
        <Label className="text-[13px]">Racontez votre histoire</Label>
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Expliquez le contexte, l'objectif et comment les fonds seront utilisés..." rows={6} className="mt-1.5 resize-none" maxLength={800} />
        <div className="text-[11px] text-neutral-500 mt-1">{description.length}/800 · minimum 10 caractères</div>
      </div>
      <div>
        <Label className="text-[13px]">Image de couverture (facultatif)</Label>
        <CoverUpload image={image} setImage={setImage} />
        <div className="mt-3 rounded-xl overflow-hidden border border-black/5 aspect-[16/8] bg-neutral-100">
          <img
            src={image || DEFAULT_IMAGES[type]}
            alt="aperçu"
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = DEFAULT_IMAGES[type]; }}
          />
        </div>
      </div>
    </div>
  );
}

export function StepGoal({ goal, setGoal, duration, setDuration, country, setCountry, accent }) {
  const goalNum = Number(goal);
  const durationNum = Number(duration);
  return (
    <div className="space-y-5">
      <div>
        <Label className="text-[13px]">Objectif en FCFA</Label>
        <Input type="number" min={1000} step={1000} value={goal} onChange={(e) => setGoal(e.target.value)} className="mt-1.5 h-12 text-[18px] font-semibold" />
        <div className="mt-2 flex flex-wrap gap-2">
          {GOAL_PRESETS.map((v) => (
            <button
              key={v}
              onClick={() => setGoal(v)}
              className={`px-3 py-1.5 rounded-full text-[12.5px] border ${goalNum === v ? 'text-white' : 'text-neutral-700 bg-white'} btn-anim`}
              style={goalNum === v ? { background: accent, borderColor: accent } : { borderColor: 'rgba(0,0,0,0.08)' }}
            >
              {formatN(v)} FCFA
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-[13px]">Durée de la collecte</Label>
        <div className="mt-1.5 flex flex-wrap gap-2">
          {DURATION_PRESETS.map((d) => (
            <button
              key={d}
              onClick={() => setDuration(d)}
              className={`px-3.5 py-2 rounded-full text-[13px] border ${durationNum === d ? 'text-white' : 'text-neutral-700 bg-white'} btn-anim`}
              style={durationNum === d ? { background: accent, borderColor: accent } : { borderColor: 'rgba(0,0,0,0.08)' }}
            >
              <Calendar className="w-3.5 h-3.5 inline mr-1.5" /> {d} jours
            </button>
          ))}
          <Input type="number" min={1} max={365} value={duration} onChange={(e) => setDuration(e.target.value)} className="h-10 w-28" />
        </div>
      </div>
      <div>
        <Label className="text-[13px]">Pays</Label>
        <div className="mt-1.5 flex flex-wrap gap-1.5 max-h-32 overflow-auto no-scrollbar">
          {COUNTRIES.map((c) => (
            <button
              key={c}
              onClick={() => setCountry(c)}
              className={`px-3 py-1.5 rounded-full text-[12.5px] border btn-anim ${country === c ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white text-neutral-700 border-black/10 hover:bg-neutral-50'}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function StepBeneficiary({ provider, setProvider, beneficiaryPhone, setBeneficiaryPhone, beneficiaryHolder, setBeneficiaryHolder }) {
  return (
    <div className="space-y-5">
      <div>
        <Label className="text-[13px]">Opérateur Mobile Money</Label>
        <RadioGroup value={provider} onValueChange={setProvider} className="mt-2 grid grid-cols-2 gap-2.5">
          {PROVIDERS.map((p) => (
            <label
              key={p.id}
              htmlFor={`prov-${p.id}`}
              className={`cursor-pointer rounded-2xl border p-4 flex items-center gap-3 btn-anim ${provider === p.id ? 'bg-white' : 'bg-white/60 border-black/5 hover:bg-white'}`}
              style={provider === p.id ? { borderColor: p.color } : {}}
            >
              <RadioGroupItem id={`prov-${p.id}`} value={p.id} />
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${p.color}18`, color: p.color }}>
                <Wallet className="w-4 h-4" />
              </div>
              <div className="text-[14px] font-medium text-neutral-800">{p.name}</div>
            </label>
          ))}
        </RadioGroup>
      </div>
      <div>
        <Label className="text-[13px]">Numéro du bénéficiaire</Label>
        <Input value={beneficiaryPhone} onChange={(e) => setBeneficiaryPhone(e.target.value)} placeholder="+225 07 00 00 00 00" className="mt-1.5 h-11" inputMode="tel" />
      </div>
      <div>
        <Label className="text-[13px]">Nom du titulaire (facultatif)</Label>
        <Input value={beneficiaryHolder} onChange={(e) => setBeneficiaryHolder(e.target.value)} placeholder="Nom complet inscrit sur le compte" className="mt-1.5 h-11" />
      </div>
      <div className="text-[12px] text-neutral-500 bg-neutral-50 rounded-lg p-3 border border-black/5">
        Les retraits sont traités vers ce compte Mobile Money après vérification (24–48h ouvrables). Aucun paiement réel n'est effectué dans cette version démo.
      </div>
    </div>
  );
}

export function StepReview({ type, category, country, title, description, image, goal, duration, provider, beneficiaryPhone, accent }) {
  const providerName = PROVIDERS.find((p) => p.id === provider)?.name || provider;
  return (
    <div className="space-y-4">
      <div className="rounded-2xl overflow-hidden border border-black/5">
        <div className="aspect-[16/8] bg-neutral-100">
          <img src={image || DEFAULT_IMAGES[type]} alt="couverture" className="w-full h-full object-cover" />
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md text-white" style={{ background: accent }}>{type}</span>
            <span className="text-[12px] text-neutral-500">{country} · {category}</span>
          </div>
          <h3 className="mt-2 text-[18px] font-semibold text-[#1a1d1a]">{title}</h3>
          <p className="mt-1 text-[13.5px] text-neutral-600 line-clamp-3">{description}</p>
          <div className="mt-4 grid grid-cols-2 gap-3 text-[12.5px]">
            <Row label="Objectif" value={`${formatN(goal)} FCFA`} />
            <Row label="Durée" value={`${duration} jours`} />
            <Row label="Opérateur" value={providerName} />
            <Row label="Bénéficiaire" value={beneficiaryPhone} />
          </div>
        </div>
      </div>
    </div>
  );
}
